/**
 * Proof in production, computed by the server from the asset's linked success cases: client-measured results from
 * live or closed cases, self-measured ones labelled "Benchmark", expected ones as targets only. Every metric carries
 * its label in words, so measured and hoped-for never read alike.
 */

import { Gauge } from 'lucide-react';

import type { AssetOut } from '@/api/types';
import type { ProofMetric } from '@/api/types/cases';
import { StateCard } from '@/ui/StateCard';

import { Badge, type BadgeTone } from '@/ui/Badge';
import { metricResult } from './proofText';
import '../materials/catalog-tabs.css';

function ProofTable({ title, rows, badge }: { title: string; rows: ProofMetric[]; badge: { tone: BadgeTone; text: string } }) {
  if (rows.length === 0) return null;
  return (
    <section className="proof-section">
      <h3>{title}</h3>
      <div className="table-scroll">
        <table className="data zebra compact" aria-label={title}>
          <thead>
            <tr>
              <th scope="col">Metric</th>
              <th scope="col">Result</th>
              <th scope="col">Case</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((m, i) => (
              <tr key={`${m.case_id}-${m.label}-${i}`}>
                <th scope="row">
                  {m.label} <Badge tone={badge.tone}>{badge.text}</Badge>
                </th>
                <td>{metricResult(m)}</td>
                <td>{m.case_label}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function caseCount(n: number): string {
  if (n === 0) return 'No success cases linked yet.';
  return `Drawn from ${n} linked success ${n === 1 ? 'case' : 'cases'}.`;
}

export function ProofTab({ asset }: { asset: AssetOut }) {
  const { proof } = asset;
  return (
    <div className="stack-md">
      <p className="hint">{caseCount(proof.cases)}</p>
      {proof.client_measured.length === 0 ? (
        <StateCard icon={<Gauge aria-hidden="true" />} title="No measured proof yet">
          <p>Results measured by a client on a live or closed success case show here.</p>
        </StateCard>
      ) : (
        <ProofTable title="Measured by clients" rows={proof.client_measured} badge={{ tone: 'success', text: 'Client measured' }} />
      )}
      <ProofTable title="Benchmarks" rows={proof.benchmarks} badge={{ tone: 'info', text: 'Benchmark' }} />
      <ProofTable title="Targets" rows={proof.targets} badge={{ tone: 'neutral', text: 'Target, not yet measured' }} />
    </div>
  );
}
